import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { LocationForm } from './LocationForm';

type AddLocationModalProps = {
  organisationId: string;
  onClose: () => void;
  onCreated: () => void;
};

/**
 * Dedicated create-location dialog — Escape / backdrop / Close dismisses.
 */
export function AddLocationModal({ organisationId, onClose, onCreated }: AddLocationModalProps) {
  const { t } = useTranslation(['displays', 'common']);
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
    };
  }, [onClose]);

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="add-location-title"
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6"
      style={{ background: 'color-mix(in srgb, #0a0a0b 55%, transparent)' }}
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg rounded-[14px] border border-line p-5 shadow-lg sm:p-6"
        style={{ background: 'var(--gs-panel)' }}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="mb-5 flex items-start justify-between gap-3">
          <div>
            <h2 id="add-location-title" className="m-0 text-lg font-bold text-ink">
              {t('location.add.title')}
            </h2>
            <p className="m-0 mt-1 text-sm text-muted">{t('location.add.description')}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 text-sm font-semibold text-muted hover:text-ink"
          >
            {t('common:actions.close')}
          </button>
        </div>

        <LocationForm
          organisationId={organisationId}
          onSaved={() => {
            onCreated();
            onClose();
          }}
        />
      </div>
    </div>,
    document.body,
  );
}
